import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Upload, Loader2, FileDown } from "lucide-react";
import { api, formatApiError, fileUrl, downloadFileBlob } from "@/lib/api";
import { toast } from "sonner";

const REQUIRED_FIELDS = ["phone", "address", "company_name", "position", "experience_years"];

export default function Profil() {
  const [profile, setProfile] = useState(null);
  const [member, setMember] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploadingField, setUploadingField] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/members/me/profile"), api.get("/members/me")])
      .then(([p, m]) => { setProfile(p.data); setForm(p.data); setMember(m.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/members/me/profile", form);
      setProfile(res.data);
      toast.success("Profil dikemas kini");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const uploadDocPhoto = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingField(field);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload?is_public=false", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const updated = await api.put("/members/me/profile", { [field]: res.data.file_id });
      setProfile(updated.data);
      setForm((f) => ({ ...f, [field]: res.data.file_id }));
      toast.success("Gambar berjaya dimuat naik");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploadingField(null);
    }
  };

  if (loading) return <div className="p-8 text-center text-piks-muted">Memuatkan profil...</div>;
  if (!profile) return <div className="p-8 text-center text-piks-muted" data-testid="profil-empty-state">Profil tidak dijumpai.</div>;

  const completion = Math.round((REQUIRED_FIELDS.filter((f) => form[f]).length / REQUIRED_FIELDS.length) * 100);

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      await downloadFileBlob("/members/me/resume-pdf", `${member?.member_number || "profil-saya"}.pdf`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div className="p-6 sm:p-10 max-w-3xl" data-testid="profil-page">
      <div className="flex items-start justify-between gap-4 mb-1 flex-wrap">
        <h1 className="font-display text-xl font-bold text-piks-black">Profil Saya</h1>
        <Button size="sm" variant="outline" onClick={downloadPdf} disabled={downloadingPdf} data-testid="profil-download-pdf-btn">
          <FileDown className="w-3.5 h-3.5 mr-1.5" /> {downloadingPdf ? "Menjana..." : "Muat Turun Profil (PDF)"}
        </Button>
      </div>
      <div className="flex items-center gap-3 mb-6">
        <p className="text-sm text-piks-muted">Profil Lengkap</p>
        <div className="flex-1 h-1.5 bg-piks-border rounded-full max-w-[160px] overflow-hidden">
          <div className="h-full bg-piks-gold" style={{ width: `${completion}%` }} />
        </div>
        <span className="text-sm font-semibold text-piks-black" data-testid="profile-completion-percent">{completion}%</span>
      </div>

      <Tabs defaultValue="peribadi">
        <TabsList className="mb-6 flex-wrap h-auto">
          <TabsTrigger value="peribadi" data-testid="tab-maklumat-peribadi">Maklumat Peribadi</TabsTrigger>
          <TabsTrigger value="profesional" data-testid="tab-maklumat-profesional">Maklumat Profesional</TabsTrigger>
          <TabsTrigger value="syarikat" data-testid="tab-maklumat-syarikat">Maklumat Syarikat</TabsTrigger>
          <TabsTrigger value="kursus-waris" data-testid="tab-kursus-waris">Kursus & Waris</TabsTrigger>
          <TabsTrigger value="privasi" data-testid="tab-tetapan-privasi">Tetapan Privasi</TabsTrigger>
        </TabsList>

        <TabsContent value="peribadi" className="space-y-4">
          <div><Label>Nama Penuh</Label><Input value={profile.full_name || ""} disabled className="mt-1.5 bg-piks-ivory" /></div>
          <div><Label>Emel</Label><Input value={profile.email || ""} disabled className="mt-1.5 bg-piks-ivory" /></div>
          <div><Label>No. Telefon</Label><Input data-testid="profile-phone-input" value={form.phone || ""} onChange={(e) => update("phone", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Alamat</Label><Input data-testid="profile-address-input" value={form.address || ""} onChange={(e) => update("address", e.target.value)} className="mt-1.5" /></div>

          <div>
            <Label>Gambar Kad Pengenalan (IC)</Label>
            <div className="mt-1.5 flex items-center gap-3">
              {form.ic_photo_file_id && <img src={fileUrl(form.ic_photo_file_id)} alt="Gambar IC" className="w-20 h-14 rounded-md object-cover border border-piks-border" />}
              <label className="flex items-center gap-2 px-4 py-2 rounded-md border border-piks-border cursor-pointer text-sm hover:bg-piks-ivory">
                {uploadingField === "ic_photo_file_id" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {form.ic_photo_file_id ? "Tukar Gambar IC" : "Muat Naik Gambar IC"}
                <input type="file" accept="image/*" className="hidden" data-testid="profile-ic-photo-input" onChange={(e) => uploadDocPhoto(e, "ic_photo_file_id")} />
              </label>
            </div>
          </div>

          <div>
            <Label>Gambar Pasport (Pilihan)</Label>
            <div className="mt-1.5 flex items-center gap-3">
              {form.passport_photo_file_id && <img src={fileUrl(form.passport_photo_file_id)} alt="Gambar Pasport" className="w-20 h-14 rounded-md object-cover border border-piks-border" />}
              <label className="flex items-center gap-2 px-4 py-2 rounded-md border border-piks-border cursor-pointer text-sm hover:bg-piks-ivory">
                {uploadingField === "passport_photo_file_id" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {form.passport_photo_file_id ? "Tukar Gambar Pasport" : "Muat Naik Gambar Pasport"}
                <input type="file" accept="image/*" className="hidden" data-testid="profile-passport-photo-input" onChange={(e) => uploadDocPhoto(e, "passport_photo_file_id")} />
              </label>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="profesional" className="space-y-4">
          <div><Label>Tahap Pendidikan</Label><Input value={profile.education_level || ""} disabled className="mt-1.5 bg-piks-ivory" /></div>
          <div><Label>Pekerjaan</Label><Input value={profile.occupation || ""} disabled className="mt-1.5 bg-piks-ivory" /></div>
          <div><Label>Jawatan</Label><Input data-testid="profile-position-input" value={form.position || ""} onChange={(e) => update("position", e.target.value)} className="mt-1.5" /></div>
          <div><Label>Tahun Pengalaman</Label><Input type="number" data-testid="profile-experience-input" value={form.experience_years || ""} onChange={(e) => update("experience_years", parseInt(e.target.value) || 0)} className="mt-1.5" /></div>
        </TabsContent>

        <TabsContent value="syarikat" className="space-y-4">
          <div><Label>Nama Syarikat</Label><Input data-testid="profile-company-input" value={form.company_name || ""} onChange={(e) => update("company_name", e.target.value)} className="mt-1.5" /></div>
        </TabsContent>

        <TabsContent value="kursus-waris" className="space-y-6">
          {profile.course_name && (
            <div>
              <p className="text-sm font-semibold text-piks-black mb-3">Maklumat Kursus</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Nama Kursus</Label><Input value={profile.course_name || ""} disabled className="mt-1.5 bg-piks-ivory" /></div>
                <div><Label>Tarikh Kursus</Label><Input value={profile.course_date || ""} disabled className="mt-1.5 bg-piks-ivory" /></div>
                <div><Label>Sesi / Tahun</Label><Input value={profile.course_session_year || ""} disabled className="mt-1.5 bg-piks-ivory" /></div>
                <div><Label>Tempat Kursus</Label><Input value={profile.course_venue || ""} disabled className="mt-1.5 bg-piks-ivory" /></div>
              </div>
            </div>
          )}
          <div>
            <p className="text-sm font-semibold text-piks-black mb-3">Maklumat Waris</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Nama Waris</Label><Input data-testid="profile-kin-name-input" value={form.next_of_kin_name || ""} onChange={(e) => update("next_of_kin_name", e.target.value)} className="mt-1.5" /></div>
              <div><Label>Hubungan</Label><Input data-testid="profile-kin-relationship-input" value={form.next_of_kin_relationship || ""} onChange={(e) => update("next_of_kin_relationship", e.target.value)} className="mt-1.5" /></div>
              <div><Label>No. Telefon Waris</Label><Input data-testid="profile-kin-phone-input" value={form.next_of_kin_phone || ""} onChange={(e) => update("next_of_kin_phone", e.target.value)} className="mt-1.5" /></div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="privasi" className="space-y-4">
          <p className="text-sm text-piks-muted mb-2">Pilih maklumat yang boleh dipaparkan secara umum pada profil ahli disahkan anda.</p>
          <PrivacyToggle label="Paparkan Syarikat" checked={!!form.public_company} onChange={(v) => update("public_company", v)} testid="toggle-public-company" />
          <PrivacyToggle label="Paparkan Jawatan" checked={!!form.public_position} onChange={(v) => update("public_position", v)} testid="toggle-public-position" />
          <PrivacyToggle label="Paparkan Profil Profesional" checked={!!form.public_professional_profile} onChange={(v) => update("public_professional_profile", v)} testid="toggle-public-professional" />
          <PrivacyToggle label="Paparkan Rekod Latihan" checked={!!form.public_training} onChange={(v) => update("public_training", v)} testid="toggle-public-training" />
        </TabsContent>
      </Tabs>

      <Button onClick={save} disabled={saving} data-testid="profile-save-btn" className="mt-6 bg-piks-black hover:bg-piks-black/90">
        {saving ? "Menyimpan..." : "Simpan Perubahan"}
      </Button>
    </div>
  );
}

function PrivacyToggle({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between p-4 rounded-lg bg-piks-ivory border border-piks-border">
      <span className="text-sm font-medium text-piks-black">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
